import React, { useState, type ComponentPropsWithoutRef } from 'react';
import {
  View,
  TextInput,
  Text,
  type ViewStyle,
  type TextStyle,
  useColorScheme,
} from 'react-native';
import { tokens } from '../../design-system/tokens';

export interface InputProps extends ComponentPropsWithoutRef<typeof TextInput> {
  label?: string;
  error?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  /** Leading adornment (e.g. a search glyph) rendered inside the field. */
  leftIcon?: React.ReactNode;
  /** Trailing adornment (e.g. a clear button) rendered inside the field. */
  rightIcon?: React.ReactNode;
}

export const Input: React.FC<InputProps> = ({
  label,
  error,
  disabled = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  style,
  multiline = false,
  accessibilityLabel,
  accessibilityHint,
  ...textInputProps
}) => {
  const scheme = useColorScheme();
  const colorScheme = scheme === 'dark' ? 'dark' : 'light';
  const colors = tokens.color.semantic[colorScheme];

  const [isFocused, setIsFocused] = useState(false);

  const hasError = !!error;

  const containerStyles = [
    styles.container,
    fullWidth && styles.fullWidth,
  ] as ViewStyle[];

  const inputContainerStyles = [
    styles.inputContainer,
    { borderColor: colors.border.primary, backgroundColor: colors.surface.secondary },
    isFocused && !hasError && { borderColor: colors.accent.indigo },
    hasError && { borderColor: colors.accent.rose },
    disabled && styles.disabled,
  ] as ViewStyle[];

  const inputStyles = [
    styles.input,
    { color: colors.text.primary },
    multiline && styles.multilineInput,
    style,
  ] as TextStyle[];

  return (
    <View style={containerStyles}>
      {label && (
        <Text
          style={[styles.label, { color: colors.text.primary }]}
          accessibilityRole="text"
        >
          {label}
        </Text>
      )}
      <View style={[inputContainerStyles, (leftIcon != null || rightIcon != null) && styles.inputContainerRow]}>
        {leftIcon != null ? <View style={styles.adornment}>{leftIcon}</View> : null}
        <TextInput
          style={[inputStyles, (leftIcon != null || rightIcon != null) && styles.inputFlex]}
          editable={!disabled}
          multiline={multiline}
          placeholderTextColor={colors.text.tertiary}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          accessibilityLabel={accessibilityLabel ?? label}
          accessibilityHint={accessibilityHint}
          accessibilityState={{ disabled }}
          {...textInputProps}
        />
        {rightIcon != null ? <View style={styles.adornment}>{rightIcon}</View> : null}
      </View>
      {hasError && (
        <Text
          style={[styles.errorText, { color: colors.accent.rose }]}
          accessibilityRole="alert"
          accessibilityLiveRegion="assertive"
        >
          {error}
        </Text>
      )}
    </View>
  );
};

const styles = {
  container: {
    gap: tokens.spacing.xs,
  } satisfies ViewStyle,

  fullWidth: {
    width: '100%',
  } satisfies ViewStyle,

  label: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.footnote,
    fontWeight: '500' as TextStyle['fontWeight'],
    lineHeight: tokens.typography.fontSize.footnote * tokens.typography.lineHeight.normal,
  } satisfies TextStyle,

  inputContainer: {
    borderWidth: 1,
    borderRadius: tokens.radius.md,
    minHeight: 30,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.xs,
    justifyContent: 'center',
  } satisfies ViewStyle,

  inputContainerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  } satisfies ViewStyle,

  inputFlex: {
    flex: 1,
  } satisfies TextStyle,

  adornment: {
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,

  input: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.footnote,
    fontWeight: '400' as TextStyle['fontWeight'],
    lineHeight: tokens.typography.fontSize.body * tokens.typography.lineHeight.normal,
    padding: 0,
    margin: 0,
  } satisfies TextStyle,

  multilineInput: {
    minHeight: 80,
    textAlignVertical: 'top',
  } satisfies TextStyle,

  disabled: {
    opacity: 0.5,
  } satisfies ViewStyle,

  errorText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.caption,
    fontWeight: '400' as TextStyle['fontWeight'],
    lineHeight: tokens.typography.fontSize.caption * tokens.typography.lineHeight.normal,
  } satisfies TextStyle,
};
